import React from 'react';
import type { ColumnProps } from '#/common/components/DataGrid';
import type { AssetPlanActiveScenarioItem } from '#/graphql/types';
import { color } from '@nuvolo/nuux-theme/utils';
import ActionChip from '#/common/components/ActionChip';
import {
  assetPlanActionColors,
  assetPlanActionItems,
  assetPlanItemTypeItems,
} from '../../utils/constants';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';

const CellChangeIndicator: React.FC<{ show: boolean }> = ({ show }) =>
  show ? (
    <span
      style={{
        position: 'absolute',
        top: -2,
        left: -2,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: 'linear-gradient(90.51deg, #25B266 -413.01%, #B1FF31 -52.18%, #FF6B00 308.64%)',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />
  ) : null;

type ActionValue = AssetPlanActiveScenarioItem['action'];

type ActionItem = {
  key: ActionValue;
  label: string;
};

type BuildColumnsOptions = {
  interventionYearOptions: number[];
  getAllowedActionItems: (row: AssetPlanActiveScenarioItem) => ActionItem[];
  onInterventionYearChange: (
    rowId: string,
    value: number | null,
    row: AssetPlanActiveScenarioItem,
  ) => void;
  getInterventionYearValue: (rowId: string, fallbackValue?: number | null) => number | null;
  getActionValue: (rowId: string, fallbackValue?: ActionValue) => ActionValue | undefined;
  onActionChange: (rowId: string, value: ActionValue, row: AssetPlanActiveScenarioItem) => void;
  isActionOverridden: (rowId: string) => boolean;
  isInterventionYearOverridden: (rowId: string) => boolean;
};

const buildColumns = ({
  getActionValue,
  getInterventionYearValue,
  interventionYearOptions,
  getAllowedActionItems,
  onActionChange,
  onInterventionYearChange,
  isActionOverridden,
  isInterventionYearOverridden,
}: BuildColumnsOptions): ColumnProps<AssetPlanActiveScenarioItem>[] => [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'itemNumber',
    allowSorting: true,
    caption: 'Number',
    visible: true,
    dataType: 'string',
    // fixed: true, // Disabled — causes blank group-row rendering (TMM-1558)
    openOnClick: true,
    path: (row) => {
      const itemType = (row.itemType || '').toLowerCase();
      const itemId = row.itemId;
      if (!itemId) {
        return undefined;
      }

      if (itemType === 'asset') {
        return `/assets/${itemId}`;
      }

      if (itemType === 'finding') {
        return `/findings/${itemId}/details`;
      }

      if (itemType === 'capital_need') {
        return `/capital-needs/${itemId}/details`;
      }
    },
  },
  {
    dataField: 'assetId',
    caption: 'Serial Number',
    visible: true,
    dataType: 'string',
    groupIndex: 0,
    sortIndex: 0,
    sortOrder: 'asc',
    groupCellRender: (r: any): React.ReactNode => {
      return (
        <NuvoBox>
          <b>{r.data.count}</b> plan items for <b>{r.data.summary[7]}</b>
        </NuvoBox>
      );
    },
  },
  {
    dataField: 'name',
    caption: 'Name',
    visible: true,
    allowSorting: true,
    dataType: 'string',
  },
  {
    dataField: 'itemType',
    caption: 'Type',
    visible: true,
    dataType: 'string',
    allowSorting: true,
    lookup: {
      dataSource: assetPlanItemTypeItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'interventionCost',
    caption: 'Intervention Cost',
    visible: true,
    allowSorting: true,
    dataType: 'number',
    alignment: 'left',
    format: {
      type: 'currency',
      precision: 2,
    },
  },
  {
    dataField: 'assetReplacementCost',
    caption: 'Asset Replacement Cost',
    visible: true,
    allowSorting: true,
    dataType: 'number',
    alignment: 'left',
    format: {
      type: 'currency',
      precision: 2,
    },
    cellRender: (e) => {
      const isAsset = e.data.itemType?.toLowerCase() === 'asset';
      if (!isAsset) return <>-</>;
      const value = typeof e.value === 'number' ? e.value : null;
      if (value == null) return '-';
      return `$${value.toFixed(2)}`;
    },
  },
  {
    dataField: 'action',
    caption: 'Action',
    visible: true,
    allowSorting: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: assetPlanActionItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    cellRender: (e) => {
      const rowId = e.data.assetPlanScenarioItemId || e.data.id;
      const currentValue = getActionValue(rowId, e.value as ActionValue | undefined);
      if (!currentValue) {
        return null;
      }

      const allowedActionItems = getAllowedActionItems(e.data);
      const actionItems = allowedActionItems.some((item) => item.key === currentValue)
        ? allowedActionItems
        : [
            ...allowedActionItems,
            assetPlanActionItems.find((item) => item.key === currentValue) ?? {
              key: currentValue,
              label: currentValue,
            },
          ];
      const customColors = assetPlanActionColors[currentValue as string];

      return (
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
          }}>
          <CellChangeIndicator show={isActionOverridden(rowId)} />
          <ActionChip
            value={currentValue}
            items={actionItems.map((item) => ({ key: String(item.key), label: item.label }))}
            customColors={customColors}
            onChange={(nextValue) => onActionChange(rowId, nextValue as ActionValue, e.data)}
          />
        </div>
      );
    },
  },
  {
    dataField: 'interventionYear',
    caption: 'Intervention Year',
    visible: true,
    dataType: 'number',
    alignment: 'left',
    allowSorting: true,
    cellRender: (e) => {
      const rowId = e.data.assetPlanScenarioItemId || e.data.id;
      const actionValue = getActionValue(rowId, e.data.action);
      const value = getInterventionYearValue(rowId, e.value as number | null | undefined);
      const hasValidOptions = interventionYearOptions.length > 0;
      const shouldHideYearSelector = actionValue === 'ignore' || actionValue === 'defer';
      const isRepairAsset = e.data.itemType?.toLowerCase() === 'asset' && actionValue === 'repair';

      if (shouldHideYearSelector) {
        return (
          <div
            style={{
              width: 32,
              padding: '5px 12px',
              borderRadius: 8,
              backgroundColor: color('custom', 'dataGridHeaderBackground'),
              color: '#ffffff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              opacity: 0.7,
              textAlign: 'center',
            }}>
            -
          </div>
        );
      }

      if (isRepairAsset) {
        return (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 24,
              padding: '8px 8px',
              borderRadius: 8,
              backgroundColor: color('custom', 'dataGridHeaderBackground'),
              color: color('custom', 'contrastText'),
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
            Multi
          </div>
        );
      }

      return (
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
          }}>
          <CellChangeIndicator show={isInterventionYearOverridden(rowId)} />
          <ActionChip
            value={value == null ? '' : String(value)}
            emptyLabel="Year"
            items={interventionYearOptions.map((year) => ({
              key: String(year),
              label: String(year),
            }))}
            menuWidth={84}
            customColors={{
              backgroundColor: color('custom', 'dataGridHeaderBackground'),
              textColor: color('custom', 'contrastText'),
            }}
            onChange={(nextRaw) => {
              if (!hasValidOptions) {
                return;
              }

              const nextValue = Number(nextRaw);
              if (!Number.isFinite(nextValue) || !interventionYearOptions.includes(nextValue)) {
                return;
              }

              onInterventionYearChange(rowId, Math.round(nextValue), e.data);
            }}
          />
        </div>
      );
    },
  },
  {
    dataField: 'score',
    caption: 'Score',
    visible: false,
    allowSorting: true,
    dataType: 'number',
  },
  {
    dataField: 'priority',
    caption: 'Priority',
    visible: false,
    allowSorting: true,
    dataType: 'number',
  },
  {
    dataField: 'assetPlanItemId',
    caption: 'Asset Plan Item ID',
    visible: false,
    showInColumnChooser: false,
    allowSorting: true,
    dataType: 'string',
  },
  {
    dataField: 'assetPlanScenarioItemId',
    caption: 'Scenario Item ID',
    visible: false,
    showInColumnChooser: false,
    allowSorting: true,
    dataType: 'string',
  },
  {
    dataField: 'hasRelatedItems',
    caption: 'Has Related Items',
    visible: false,
    showInColumnChooser: false,
    allowSorting: true,
    dataType: 'boolean',
  },
];

export default buildColumns;
